import { GetProposalQuery } from '@/gql/graphql';
import { type ProposalLifecycleActionKey } from '@/ui/pages/proposalBuilderPage/components/proposalLifecycleActions/lifecycleActions';
import { type ProposalLifecycleMenuAction } from '@/ui/pages/proposalBuilderPage/components/proposalLifecycleActions/useProposalLifecycleActions';
import { TIconName } from '@payaca/untitled-icons';

/**
 * Mark-as-sent never reaches this list — it only renders on a Draft — so the
 * lifecycle keys here are the post-send ones.
 */
export type SentProposalActionKey =
  | Exclude<ProposalLifecycleActionKey, 'markAsSent'>
  | 'resend'
  | 'duplicate'
  | 'previewAsCustomer'
  | 'downloadPdf';

export type SentProposalAction = {
  key: SentProposalActionKey;
  label: string;
  iconName: TIconName;
  disabled: boolean;
  disabledReasons: string[];
  onClick: () => void;
};

export type SentProposalActionAvailability =
  GetProposalQuery['proposal']['actionAvailability'];

const ICON: Record<SentProposalActionKey, TIconName> = {
  resend: 'send-01.3',
  markAsAccepted: 'check-circle.3',
  markAsDeclined: 'x-circle.3',
  removeAccepted: 'user-x-01',
  replace: 'edit-01.3',
  duplicate: 'copy-07.3',
  previewAsCustomer: 'eye.3',
  downloadPdf: 'download-01.3',
};

/**
 * One order for every status, so a row sits where the reader last saw it. A
 * status only ever removes rows from this list — it never reorders them.
 */
export const ORDER: SentProposalActionKey[] = [
  'previewAsCustomer',
  'downloadPdf',
  'resend',
  'markAsAccepted',
  'markAsDeclined',
  'removeAccepted',
  'replace',
  'duplicate',
];

type BuildInput = {
  /** Already built by `useProposalLifecycleActions`, handlers and all. */
  lifecycleActions: ProposalLifecycleMenuAction[];
  availability: SentProposalActionAvailability | undefined;
  /** Without the permission the row is dropped, as the actions menu drops it. */
  canDuplicate: boolean;
  /** Dropped rather than disabled: a preview is not this Proposal's to offer. */
  canPreviewAsCustomer: boolean;
  /** False only until the Proposal resolves, since the PDF needs its job id. */
  canDownloadPdf: boolean;
  isDownloadingPdf: boolean;
  /** Why the Project cannot be previewed. Disables the row when non-empty. */
  previewBlockedReasons: string[];
  onResend: () => void;
  onDuplicate: () => void;
  onPreviewAsCustomer: () => void;
  onDownloadPdf: () => void;
};

const toAction = (input: {
  key: SentProposalActionKey;
  label: string;
  disabled: boolean;
  disabledReasons: string[];
  onClick: () => void;
}): SentProposalAction => ({
  ...input,
  iconName: ICON[input.key],
  // MenuItemCore renders reasons only when disabled.
  disabledReasons: input.disabled ? input.disabledReasons : [],
});

export const buildSentProposalActions = (
  input: BuildInput
): SentProposalAction[] => {
  const {
    lifecycleActions,
    availability,
    canDuplicate,
    canPreviewAsCustomer,
    canDownloadPdf,
    isDownloadingPdf,
    previewBlockedReasons,
    onResend,
    onDuplicate,
    onPreviewAsCustomer,
    onDownloadPdf,
  } = input;

  const candidates = new Map<SentProposalActionKey, SentProposalAction>();

  for (const action of lifecycleActions) {
    if (action.actionKey === 'markAsSent') continue;
    candidates.set(
      action.actionKey,
      toAction({ ...action, key: action.actionKey })
    );
  }

  // Availability is unknown until the Proposal query resolves. Leave the
  // action enabled rather than showing a reasonless disabled row; the mutation
  // re-checks the same gate server-side.
  const resend = availability?.resend;
  const duplicate = availability?.duplicate;

  candidates.set(
    'resend',
    toAction({
      key: 'resend',
      label: 'Resend',
      disabled: resend ? !resend.result : false,
      disabledReasons: resend?.reasons ?? [],
      onClick: onResend,
    })
  );

  if (canDuplicate) {
    candidates.set(
      'duplicate',
      toAction({
        key: 'duplicate',
        label: 'Duplicate',
        disabled: duplicate ? !duplicate.result : false,
        disabledReasons: duplicate?.reasons ?? [],
        onClick: onDuplicate,
      })
    );
  }

  if (canPreviewAsCustomer) {
    candidates.set(
      'previewAsCustomer',
      toAction({
        key: 'previewAsCustomer',
        label: 'Preview as customer',
        disabled: previewBlockedReasons.length > 0,
        disabledReasons: previewBlockedReasons,
        onClick: onPreviewAsCustomer,
      })
    );
  }

  candidates.set(
    'downloadPdf',
    toAction({
      key: 'downloadPdf',
      label: 'Download PDF',
      disabled: !canDownloadPdf || isDownloadingPdf,
      disabledReasons: [],
      onClick: onDownloadPdf,
    })
  );

  return ORDER.map((key) => candidates.get(key)).filter(
    (action) => action !== undefined
  );
};
